import { faArrowLeft } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Activity } from '@spike/activity-model';
import clsx from 'clsx';
import React from 'react';
import { OverDetail } from './OverDetail/OverDetail';
import { GeneralNotesField } from './GeneralNotesField';
import { RecentActivitiesPanel } from './RecentActivitiesPanel/RecentActivitiesPanel';
import { ReportActivityForm } from './ReportActivityForm/ReportActivityForm';
import { ReportBuilderProvider, useReportBuilder } from './ReportBuilderProvider';
import { ReportFooter } from './ReportFooter';
import { ReportHeader } from './ReportHeader/ReportHeader';
import { ReportTabs, TabOption } from './ReportTabs';
import { SendReportModal } from './SendReportModal';
import { TimelinePreview } from './TimelinePreview';
import { ReportBuilderProps } from './types';
import styles from './ReportBuilder.module.scss';

// ---- Inner shell (reads provider state) ----

type ControlTab = 'add-activity' | 'recent';
type MobileTab = 'report' | 'add-activity' | 'recent';

const CONTROL_TABS: TabOption<ControlTab>[] = [
    { id: 'add-activity', name: 'Add Activity' },
    { id: 'recent', name: 'Recent Activities' }
];

const MOBILE_TABS: TabOption<MobileTab>[] = [
    { id: 'report', name: 'Report' },
    { id: 'add-activity', name: 'Add Activity' },
    { id: 'recent', name: 'Recent Activities' }
];

const ReportBuilderInner: React.FC<ReportBuilderProps> = ({
    pet,
    petParentName,
    petParentPhone,
    petParentEmail,
    appointmentId: _appointmentId,
    bookingId,
    businessAreaId,
    serviceFrom,
    serviceTo,
    onClose
}) => {
    const { saveReport, sendReport, reportDate } = useReportBuilder();

    const [controlTab, setControlTab] = React.useState<ControlTab>('add-activity');
    const [mobileTab, setMobileTab] = React.useState<MobileTab>('report');
    const [activityToEdit, setActivityToEdit] = React.useState<Activity | undefined>(undefined);
    const [isSendModalOpen, setIsSendModalOpen] = React.useState(false);

    const serviceContext = businessAreaId === 'boarding' ? 'boarding' : 'daycare';
    const reportTitle = serviceContext === 'boarding' ? 'Boarding Report' : 'Daycare Report';

    const handleSave = React.useCallback(async () => {
        await saveReport().catch(() => {
            // error shown inline via provider state
        });
    }, [saveReport]);

    const handleSend = React.useCallback(() => {
        setIsSendModalOpen(true);
    }, []);

    const handleConfirmSend = React.useCallback((email: string) => sendReport(email), [sendReport]);

    const handleCloseSendModal = React.useCallback(() => {
        setIsSendModalOpen(false);
    }, []);

    const handleEdit = React.useCallback((activity: Activity) => {
        setActivityToEdit(activity);
        setControlTab('add-activity');
        setMobileTab('add-activity');
    }, []);

    const handleEditDone = React.useCallback(() => {
        setActivityToEdit(undefined);
    }, []);

    const dateLimits = React.useMemo(() => ({ from: serviceFrom, to: serviceTo }), [serviceFrom, serviceTo]);

    const addActivityPanel = (
        <ReportActivityForm
            key={activityToEdit?.uuid ?? 'new'}
            activityToEdit={activityToEdit}
            petId={pet.id}
            bookingId={bookingId}
            dateLimits={dateLimits}
            onDone={handleEditDone}
        />
    );

    const handleAddActivityFromEmpty = React.useCallback(() => {
        setControlTab('add-activity');
        setMobileTab('add-activity');
    }, []);

    const recentPanel = (
        <RecentActivitiesPanel
            petName={pet.name}
            onAddActivity={handleAddActivityFromEmpty}
        />
    );

    const previewPanel = (
        <div className={styles.previewPanel}>
            <GeneralNotesField />
            <TimelinePreview onEdit={handleEdit} />
        </div>
    );

    return (
        <div className={styles.shell}>
            {/* Header bar */}
            <div className={styles.topBar}>
                <div className={styles.topBarTitleRow}>
                    <button
                        type="button"
                        className={styles.backButton}
                        onClick={onClose}
                        aria-label="Close report builder"
                    >
                        <FontAwesomeIcon
                            icon={faArrowLeft}
                            size="xl"
                        />
                    </button>
                    <h1 className={styles.reportTitle}>{reportTitle}</h1>
                </div>

                <ReportHeader
                    pet={pet}
                    petParentName={petParentName}
                    petParentPhone={petParentPhone}
                    reportDate={reportDate}
                />
            </div>

            {/* Desktop split layout (min-width desktop) */}
            <div className={styles.desktopLayout}>
                <div className={styles.desktopLeft}>
                    <ReportTabs
                        options={CONTROL_TABS}
                        selected={controlTab}
                        onSelect={setControlTab}
                    />
                    <div className={styles.panelContent}>
                        {controlTab === 'add-activity' ? addActivityPanel : recentPanel}
                    </div>
                </div>
                <div className={styles.desktopRight}>
                    {previewPanel}
                    <ReportFooter
                        className={styles.footerDesktop}
                        onSave={handleSave}
                        onSend={handleSend}
                    />
                </div>
            </div>

            {/* Mobile tabbed layout */}
            <div className={styles.mobileLayout}>
                <ReportTabs
                    options={MOBILE_TABS}
                    selected={mobileTab}
                    onSelect={setMobileTab}
                    className={styles.tabsMobile}
                />
                <div className={styles.mobilePanelContent}>
                    {mobileTab === 'report' && previewPanel}
                    {mobileTab === 'add-activity' && addActivityPanel}
                    {mobileTab === 'recent' && recentPanel}
                </div>
            </div>

            <ReportFooter
                className={styles.footerMobile}
                onSave={handleSave}
                onSend={handleSend}
            />

            {isSendModalOpen && (
                <SendReportModal
                    defaultEmail={petParentEmail}
                    onConfirm={handleConfirmSend}
                    onClose={handleCloseSendModal}
                />
            )}
        </div>
    );
};

// ---- Public shell (wraps provider) ----

const ANIMATION_DURATION = 400;

export const ReportBuilder: React.FC<ReportBuilderProps> = props => {
    const { onClose } = props;
    const [isAnimating, setIsAnimating] = React.useState(false);

    React.useEffect(() => {
        const frame = requestAnimationFrame(() => {
            setIsAnimating(true);
        });
        return () => cancelAnimationFrame(frame);
    }, []);

    const animatedClose = React.useCallback(() => {
        setIsAnimating(false);

        setTimeout(() => {
            onClose();
        }, ANIMATION_DURATION);
    }, [onClose]);

    return (
        <OverDetail
            fullWindow
            fullHeight
            withoutScroll
            className={clsx(styles.overDetailTransparent, { [styles.backdropVisible]: isAnimating })}
        >
            <div
                className={clsx(styles.animatedRoot, { [styles.open]: isAnimating })}
                style={{ '--animation-duration': `${ANIMATION_DURATION}ms` } as React.CSSProperties}
            >
                <ReportBuilderProvider
                    appointmentId={props.appointmentId}
                    bookingId={props.bookingId}
                    petId={props.pet.id}
                    datePeriod={{ from: props.serviceFrom, to: props.serviceTo }}
                    serviceContext={props.businessAreaId === 'boarding' ? 'boarding' : 'daycare'}
                    initialPetAvatar={props.pet.image}
                    petParentEmail={props.petParentEmail}
                >
                    <ReportBuilderInner
                        {...props}
                        onClose={animatedClose}
                    />
                </ReportBuilderProvider>
            </div>
        </OverDetail>
    );
};
